<script setup>
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAttentionPoints from './PostSaleAttentionPoints.vue';
import PostSaleDeadlineMap from './PostSaleDeadlineMap.vue';
import PostSaleNotesPanel from './PostSaleNotesPanel.vue';
import PostSaleSalesDetailActions from './PostSaleSalesDetailActions.vue';
import PostSaleStatusAnalysisTopics from './PostSaleStatusAnalysisTopics.vue';
import PostSaleStatusTimeline from './PostSaleStatusTimeline.vue';

defineProps({
  agents: { type: Array, default: () => [] },
  assigning: { type: Boolean, default: false },
  cancelling: { type: Boolean, default: false },
  canManageNotes: { type: Boolean, default: false },
  canManageTopics: { type: Boolean, default: false },
  caseData: { type: Object, default: null },
  completing: { type: Boolean, default: false },
  editingNote: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  loadingAgents: { type: Boolean, default: false },
  loadingNotes: { type: Boolean, default: false },
  loadingTopics: { type: Boolean, default: false },
  notes: { type: Array, default: () => [] },
  savingNote: { type: Boolean, default: false },
  showNoteForm: { type: Boolean, default: false },
  topics: { type: Array, default: () => [] },
  topicCatalog: { type: Object, default: () => ({}) },
});
const emit = defineEmits([
  'assign',
  'back',
  'cancel',
  'changeStatus',
  'deadlineAction',
  'complete',
  'correction',
  'createNote',
  'closeNoteForm',
  'deleteNote',
  'deleteTopic',
  'editAnalysis',
  'editNote',
  'editTopic',
  'nucleo',
  'openAnalysis',
  'saveNote',
  'selectStatusEvent',
]);
const { t } = useI18n();
</script>

<template>
  <main
    class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 py-6 sm:px-6 lg:px-10"
  >
    <div
      v-if="loading"
      class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.COMMON.LOADING') }}
    </div>
    <section
      v-else-if="!caseData"
      class="rounded-lais border border-lais-danger/30 bg-white p-6 text-center"
    >
      <p class="text-sm text-lais-danger">
        {{ t('POST_SALE.ALERTS.LOAD_ERROR') }}
      </p>
      <LaisPrimaryButton class="mt-4" variant="ghost" @click="emit('back')">
        {{ t('SALES.ACTIONS.BACK') }}
      </LaisPrimaryButton>
    </section>
    <template v-else>
      <PostSaleSalesDetailActions
        :agents="agents"
        :assigning="assigning"
        :cancelling="cancelling"
        :completing="completing"
        :case-data="caseData"
        :loading-agents="loadingAgents"
        :show-status="false"
        @assign="emit('assign', $event)"
        @cancel="emit('cancel', $event)"
        @complete="emit('complete')"
        @correction="emit('correction')"
        @nucleo="emit('nucleo')"
      />
      <PostSaleStatusTimeline
        :case-data="caseData"
        @change-status="emit('changeStatus')"
        @open-analysis="emit('openAnalysis')"
        @select="emit('selectStatusEvent', $event)"
      />
      <div
        data-testid="post-sale-status-workspace"
        class="grid items-start gap-5 lg:grid-cols-[minmax(0,513px)_minmax(0,867px)]"
      >
        <PostSaleStatusAnalysisTopics
          :can-manage="canManageTopics"
          :catalog="topicCatalog"
          :loading="loadingTopics"
          :topics="topics"
          @delete="emit('deleteTopic', $event)"
          @edit="emit('editTopic', $event)"
          @edit-analysis="emit('editAnalysis')"
        />
        <div class="grid min-w-0 gap-5">
          <PostSaleDeadlineMap
            :case-data="caseData"
            @action="emit('deadlineAction', $event)"
          />
          <PostSaleAttentionPoints
            :loading="loadingTopics"
            :topics="topics"
            @edit="emit('editTopic', $event)"
          />
        </div>
      </div>
      <PostSaleNotesPanel
        :can-manage="canManageNotes"
        :editing-note="editingNote"
        :loading="loadingNotes"
        :notes="notes"
        :saving="savingNote"
        :show-form="showNoteForm"
        @close-form="emit('closeNoteForm')"
        @create="emit('createNote')"
        @delete="emit('deleteNote', $event)"
        @edit="emit('editNote', $event)"
        @save="emit('saveNote', $event)"
      />
    </template>
  </main>
</template>
